"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import Tabs, {
  TAB_ORIENTATION,
  TAB_STYLE,
  TabItem,
} from "@/components/common/Tab";
import SubpageBackground from "@/components/common/SubpageBackground";
import PageTitle from "@/components/common/PageTitle";

export default function ServiceLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  const tabs1: TabItem[] = [
    {
      id: "application-security-analysis",
      name: "Application Security Analysis",
      value: "/service/defend/application-security-analysis",
      href: "/service/defend/application-security-analysis",
    },
    {
      id: "secure-code-review",
      name: "Secure Code Review",
      value: "/service/defend/secure-code-review",
      href: "/service/defend/secure-code-review",
    },
    {
      id: "offensive-mdr",
      name: "Offensive MDR",
      value: "/service/defend/offensive-mdr",
      href: "/service/defend/offensive-mdr",
    },
  ];

  const tabs2: TabItem[] = [
    {
      id: "pci-compliance",
      name: "PCI Compliance",
      value: "/service/defend/pci-compliance",
      href: "/service/defend/pci-compliance",
    },
    {
      id: "iso-standard",
      name: "ISO Standard",
      value: "/service/defend/iso-standard",
      href: "/service/defend/iso-standard",
    },
    {
      id: "local-regulatory",
      name: "Local Regulatory",
      value: "/service/defend/local-regulatory",
      href: "/service/defend/local-regulatory",
    },
  ];

  // Determine the title based on the current path
  const getTitle = () => {
    if (pathname.includes("application-security-analysis"))
      return "Application Security Analysis";
    if (pathname.includes("secure-code-review")) return "Secure Code Review";
    if (pathname.includes("offensive-mdr")) return "Offensive MDR";
    if (pathname.includes("pci-compliance")) return "PCI Compliance";
    if (pathname.includes("iso-standard")) return "ISO Standard";
    if (pathname.includes("local-regulatory")) return "Local Regulatory";
    return "Security Services";
  };

  return (
    <div className="relative flex flex-col bg-background">
      {/* Background Image - Matches service layout implementation */}
      {(pathname.includes("application-security-analysis") ||
        pathname.includes("secure-code-review") ||
        pathname.includes("offensive-mdr") ||
        pathname.includes("pci-compliance") ||
        pathname.includes("iso-standard") ||
        pathname.includes("local-regulatory") ||
        pathname.includes("regulatory-compliance")) && (
        /* Defend honeycomb + circuit background per Figma (node 731:75042),
           rendered at 30% opacity to match the design. */
        <SubpageBackground
          src="/images/defend-background.png"
          width={1024}
          height={683}
          className="opacity-30"
        />
      )}

      <div className="relative z-10 flex flex-col flex-1">
        {/* Breadcrumb — the red accent line fills the left gutter (from the screen
            edge to the title's accent bar) and the text aligns with the title text.
            Header is fixed (h-72): 72px clearance + ~56px gap. */}
        <div className="w-full px-4 sm:px-6 lg:px-8 pt-[112px] md:pt-[128px] overflow-hidden">
          <div className="relative max-w-[1300px] mx-auto">
            {/* Runs from the container's left edge (= title accent bar) leftward to
                the screen edge; the parent's overflow-hidden clips the overhang. */}
            <span
              aria-hidden
              className="pointer-events-none absolute top-1/2 right-full -translate-y-1/2 h-[2px] w-screen bg-primary"
            />
            <nav className="flex flex-wrap items-center gap-1 border-l-[5px] border-transparent pl-4 md:pl-6 text-sm tracking-[0.35px] text-white">
              <Link href="/service" className="transition-colors hover:text-text-default">Services</Link>
              <span>/</span>
              <Link href="/service" className="transition-colors hover:text-text-default">Defend</Link>
            </nav>
          </div>
        </div>

        {/* Title and Tab Section - Added more spacing */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          {[
            "application-security-analysis",
            "secure-code-review",
            "offensive-mdr",
          ].some((value) => pathname.includes(value)) && (
            <section className="w-full max-w-[1300px] mx-auto pb-6 pt-8 md:pb-[48px]">
              {/* mobileLines=2: "Application Security Analysis" wraps below `sm`. */}
              <PageTitle className="mb-4 md:mb-6" mobileLines={2}>
                {getTitle()}
              </PageTitle>

              {/* Tab Navigation - Added gap below */}
              <Tabs
                tabs={tabs1}
                currentValue={pathname}
                style={TAB_STYLE.STYLE_1}
                showContent={false}
                useRouter={true}
                orientation={TAB_ORIENTATION.HORIZONTAL}
                customClassName="[&>a:first-child]:!pl-0"
              />
            </section>
          )}

          {["pci-compliance", "iso-standard", "local-regulatory"].some(
            (value) => pathname.includes(value)
          ) && (
            <section className="w-full max-w-[1300px] mx-auto pb-6 pt-8 md:pb-[48px]">
              {/* PCI Compliance / ISO Standard / Local Regulatory all fit on one
                  line at 390px, so nothing needs reserving here. */}
              <PageTitle className="mb-4 md:mb-6">{getTitle()}</PageTitle>
              <Tabs
                tabs={tabs2}
                currentValue={pathname}
                style={TAB_STYLE.STYLE_1}
                showContent={false}
                useRouter={true}
                orientation={TAB_ORIENTATION.HORIZONTAL}
                customClassName="[&>a:first-child]:!pl-0"
              />
            </section>
          )}
        </div>

        {/* Page Content - Added top spacing for separation */}
        <main className="flex-1 w-full">{children}</main>
      </div>
    </div>
  );
}
